import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslationService } from '@/layout/service/translation.service';
import { RoomUsageState, roomAvailabilityLine, roomStatus } from '@/cloud/models/room-usage.models';
import { RoomStatusPillComponent } from './room-status-pill.component';

const AVAILABILITY_ICON: Record<RoomUsageState['availability'], string> = {
    Booked: 'fa fa-calendar-check',
    BookedSoon: 'fa fa-calendar-day',
    Free: 'fa fa-calendar',
    Unknown: 'fa fa-calendar-xmark'
};

/** Whether a timestamp falls on today's local date (times from other days also show the date). */
function isToday(iso: string, nowMs = Date.now()): boolean {
    const d = new Date(iso);
    const now = new Date(nowMs);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

/**
 * One consistent rendering of a room's live usage: the room's single status pill (roomStatus:
 * In a call, In a meeting, amber Booked but vacant, Occupied, ...), "3 / 8 people" when counted,
 * and a one-line calendar summary ("In a meeting until 11:00 · Team Standup", "Booked until 11:00"
 * for a booked room nobody is in, "Next at 10:00 · …", "Free until 14:00", "Free for the rest of
 * the day"). When `stale` is set (the room's control system is offline) the summary is muted and
 * says when it was last reported. Used by the room cards, the room Overview usage card and the
 * "Room usage now" attention list.
 */
@Component({
    selector: 'app-room-usage-summary',
    standalone: true,
    imports: [DatePipe, RoomStatusPillComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="usage-row">
            <app-room-status-pill class="usage-chip" [status]="status()" [stale]="stale()" [attr.data-state]="usage().occupancy" />
            @if (showPeople() && people() !== null) {
                <span class="usage-people">
                    <strong>{{ people() }}</strong>
                    @if (capacityShown()) {
                        / {{ capacityShown() }}
                    }
                    {{ t()('roomUsage.people') }}
                </span>
            }
        </div>
        @if (line(); as line) {
            <div class="usage-availability" [attr.data-availability]="usage().availability" [attr.data-status]="status()">
                <i [class]="availabilityIcon()" aria-hidden="true"></i>
                <span class="usage-availability__text">
                    @switch (line.kind) {
                        @case ('inMeeting') {
                            {{ t()('roomUsage.inMeetingUntil') }} {{ line.time | date: 'shortTime' }}
                        }
                        @case ('bookedUntil') {
                            {{ t()('roomUsage.bookedUntil') }} {{ line.time | date: 'shortTime' }}
                        }
                        @case ('nextAt') {
                            {{ t()('roomUsage.nextAt') }} {{ line.time | date: 'shortTime' }}
                        }
                        @case ('freeUntil') {
                            {{ t()('roomUsage.freeUntil') }} {{ line.time | date: 'shortTime' }}
                        }
                        @case ('freeRestOfDay') {
                            {{ t()('roomUsage.freeRestOfDay') }}
                        }
                        @default {
                            {{ t()('roomUsage.availability.Unknown') }}
                        }
                    }
                    @if (line.title) {
                        · <span class="usage-title">{{ line.title }}</span>
                    }
                </span>
            </div>
        }
        @if (stale()) {
            <div class="usage-stale" [title]="t()('roomUsage.notLive')">
                <i class="pi pi-history" aria-hidden="true"></i>
                {{ t()('roomUsage.lastReported') }} {{ usage().updatedUtc | date: updatedFormat() }}
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-2);
                min-width: 0;
                font-size: 0.875rem;
                overflow-wrap: anywhere;
            }
            :host(.room-usage-summary--compact) {
                gap: var(--app-space-1);
                font-size: 0.8125rem;
            }
            .usage-row,
            .usage-availability,
            .usage-stale {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: var(--app-space-2);
                min-width: 0;
            }
            .usage-people {
                color: var(--text-color-secondary);
                font-variant-numeric: tabular-nums;
            }
            .usage-people strong {
                color: var(--text-color);
                font-weight: 600;
            }
            .usage-availability {
                flex-wrap: nowrap;
                align-items: baseline;
                color: var(--text-color);
            }
            .usage-availability > i {
                flex-shrink: 0;
                color: var(--app-status-unknown-fg);
            }
            .usage-availability[data-availability='Booked'] > i,
            .usage-availability[data-availability='BookedSoon'] > i {
                color: var(--app-status-pending-fg);
            }
            .usage-availability[data-status='bookedVacant'] > i {
                color: var(--app-status-warning-fg);
            }
            .usage-availability[data-availability='Free'] > i {
                color: var(--app-status-online-fg);
            }
            .usage-availability__text {
                min-width: 0;
            }
            .usage-title {
                color: var(--text-color-secondary);
            }
            .usage-stale {
                color: var(--text-color-secondary);
                font-size: 0.75rem;
            }
            :host(.room-usage-summary--stale) .usage-row,
            :host(.room-usage-summary--stale) .usage-availability {
                opacity: 0.7;
            }
            :host(.room-usage-summary--stale) .usage-availability > i {
                color: var(--app-status-unknown-fg);
            }
        `
    ],
    host: {
        class: 'room-usage-summary',
        '[class.room-usage-summary--stale]': 'stale()',
        '[class.room-usage-summary--compact]': 'compact()'
    }
})
export class RoomUsageSummaryComponent {
    readonly t = inject(TranslationService).t;

    readonly usage = input.required<RoomUsageState>();
    /** The room's control system is offline: show the last reported state, muted, not as live. */
    readonly stale = input(false);
    /** Tighter spacing for tiles. */
    readonly compact = input(false);
    /** Show "3 / 8 people" (cards render people and capacity in their own row). */
    readonly showPeople = input(true);
    /** Effective capacity (cloud-set, else sensor-reported); defaults to the sensor-reported one. */
    readonly capacity = input<number | null | undefined>(undefined);

    readonly status = computed(() => roomStatus(this.usage()));
    readonly availabilityIcon = computed(() => AVAILABILITY_ICON[this.usage().availability] ?? AVAILABILITY_ICON.Unknown);
    readonly people = computed(() => {
        const count = this.usage().peopleCount;
        return typeof count === 'number' ? count : null;
    });
    readonly capacityShown = computed(() => {
        const capacity = this.capacity();
        return capacity === undefined ? (this.usage().capacity ?? null) : capacity;
    });
    readonly line = computed(() => {
        const line = roomAvailabilityLine(this.usage());
        if (!line) return null;
        return { kind: line.kind, time: 'time' in line ? line.time : null, title: 'title' in line ? line.title : null };
    });
    readonly updatedFormat = computed(() => (isToday(this.usage().updatedUtc) ? 'shortTime' : 'short'));
}
